// The one place the admin talks to the API. Everything else goes
// through apiFetch / ApiError so a cross-cutting change (new header,
// new redirect, new 401 handling) lives in one file.
//
// Shape of a request:
//   - Authorization: Bearer <jwt from sessionStorage>
//   - Content-Type: application/json          (for POST/PUT/DELETE)
//   - Idempotency-Key: crypto.randomUUID()    (for POST/PUT/DELETE)
//
// Shape of a failure:
//   - a non-2xx response throws an ApiError with status + message
//   - a 401 clears the stored token and triggers the auth context's
//     logout path (the context subscribes to this event)
//
// There is no fetch retry here. The API's rules forbid retrying
// mutations without an idempotency check (CLAUDE.md "Hard rules");
// the server already handles that correctly, but the client
// repeating a request without knowing the semantics would double
// side-effects.

export const API_BASE =
  (import.meta.env.VITE_API_BASE_URL as string | undefined)?.replace(/\/+$/, '') ?? '/v1';

export const TOKEN_STORAGE_KEY = 'tabletop.jwt';

const UNAUTHORIZED_EVENT = 'tabletop:unauthorized';

export function emitUnauthorized(): void {
  globalThis.dispatchEvent(new Event(UNAUTHORIZED_EVENT));
}

export function onUnauthorized(cb: () => void): () => void {
  globalThis.addEventListener(UNAUTHORIZED_EVENT, cb);
  return () => globalThis.removeEventListener(UNAUTHORIZED_EVENT, cb);
}

export class ApiError extends Error {
  public readonly status: number;
  public readonly body: unknown;
  constructor(status: number, message: string, body: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.body = body;
  }
}

export interface ApiFetchInit {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  // Override the generated idempotency key (tests, retry flows).
  readonly idempotencyKey?: string;
  // Skip the bearer token (login endpoint).
  readonly anonymous?: boolean;
}

function readToken(): string | null {
  try {
    return sessionStorage.getItem(TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

export async function apiFetch<T>(path: string, init: ApiFetchInit = {}): Promise<T> {
  const method = init.method ?? 'GET';
  const headers: Record<string, string> = { accept: 'application/json' };

  if (!init.anonymous) {
    const token = readToken();
    if (token) headers['authorization'] = `Bearer ${token}`;
  }

  const bodyProvided = init.body !== undefined;
  if (bodyProvided) {
    headers['content-type'] = 'application/json';
  }

  const mutating =
    method === 'POST' || method === 'PUT' || method === 'PATCH' || method === 'DELETE';
  if (mutating) {
    headers['idempotency-key'] = init.idempotencyKey ?? crypto.randomUUID();
  }

  const url = path.startsWith('http')
    ? path
    : `${API_BASE}${path.startsWith('/') ? path : `/${path}`}`;
  const fetchInit: RequestInit = { method, headers };
  if (init.signal) fetchInit.signal = init.signal;
  if (bodyProvided) fetchInit.body = JSON.stringify(init.body);

  const res = await fetch(url, fetchInit);

  if (res.status === 401) {
    try {
      sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch {
      /* ignore */
    }
    emitUnauthorized();
  }

  if (!res.ok) {
    const body = await res.text();
    let parsed: unknown = body;
    let message = `${res.status} ${res.statusText}`;
    try {
      parsed = JSON.parse(body);
      const maybe = parsed as { message?: unknown; error?: unknown };
      if (typeof maybe.message === 'string') message = maybe.message;
      else if (typeof maybe.error === 'string') message = maybe.error;
    } catch {
      /* body wasn't JSON — keep the raw text */
    }
    throw new ApiError(res.status, message, parsed);
  }

  if (res.status === 204) return undefined as T;
  const text = await res.text();
  if (text === '') return undefined as T;
  return JSON.parse(text) as T;
}
